import * as json from 'pkg/json';
import { getParsedRoutes } from 'pkg/router/parser';
import type { ParsedRoute } from 'pkg/router/parser';

const ALIAS_SELF = '@self';
const ALIAS_ROOT = '@root';

const ROUTER_CONFIRM_NAVIGATION_KEY = '__router:confirmNavigation';

interface TransformPathParams {
	href?: string;
	to?: string;
	replace?: string;
	params?: Record<string, string | number | boolean>;
	props?: Record<string, unknown>;
}

interface TransformPathResult {
	href: string;
	route: Partial<ParsedRoute>;
}

/**
 *	Adds state as querystring to a path. If shouldStringify is true it converts state to json.
 *
 *	@param string location
 *	@param string|object state
 *
 *	@returns string
 */
export function link(
	location: string,
	state: Record<string, unknown> | string = {},
	shouldSerialize: boolean = true
): string {
	if (typeof state === 'object') {
		if (Object.keys(state)?.length > 0) {
			if (shouldSerialize) {
				const params = new URLSearchParams();

				Object.entries(state).forEach(([key, value]) => {
					if (typeof value !== 'string') {
						value = json.stringify(value);
					}

					params.append(key, value?.toString() || '');
				});

				location += `?${params}`;
			} else {
				const qs = new URLSearchParams(state as Record<string, string>);
				location += `?${qs}`;
			}
		}
	} else if (!state.startsWith('?')) {
		location += `?${state}`;
	} else {
		location += state;
	}

	return location;
}

export function transform(
	path: string,
	params: Record<string, string | number | boolean> = {}
): string {
	Object.entries(params).forEach(([param, value]) => {
		path = path.replace(`:${param}`, String(value));
	});

	return path;
}

export const transformPath = (
	{ href, to, replace, params, props = {} }: TransformPathParams,
	currentRoute: Partial<ParsedRoute> | null = null
): TransformPathResult => {
	const parsedRoutes = getParsedRoutes();

	if (href && !isLocalPathOrUrl(href)) {
		return {
			href,
			route: {},
		};
	}

	if (!href && !to && !replace) {
		return {
			href: '/',
			route: {},
		};
	}

	if (!params) {
		params = {};
	}

	if (to || replace) {
		let namedRoute: ParsedRoute | null = null;

		if (to) {
			namedRoute = parsedRoutes.find((n) => n.name === to) || null;
		} else if (replace) {
			namedRoute = parsedRoutes.find((n) => n.name === replace) || null;
		}

		if (to === ALIAS_ROOT || replace === ALIAS_ROOT) {
			namedRoute = parsedRoutes.find((n) => n.guid === ALIAS_ROOT) || null;
		}

		if (to === ALIAS_SELF || replace === ALIAS_SELF) {
			if (currentRoute) {
				namedRoute = currentRoute as ParsedRoute;

				Object.entries(currentRoute).forEach(([prop, value]) => {
					if (namedRoute?.pattern?.includes(`:${prop}`)) {
						params![prop] = value as string | number | boolean;
					}
				});

				if (currentRoute.pattern) {
					namedRoute.path = currentRoute.pattern;
				}
			}
		}

		if (!namedRoute) {
			return {
				href: '/',
				route: {},
			};
		}

		let { path } = namedRoute;

		let query: Record<string, string> =
			(namedRoute.query as Record<string, string>) || {};

		const propBlacklist = ['className', 'forwardedRef', 'ref'];

		Object.entries(props || {}).forEach(([prop, value]) => {
			if (path.includes(`:${prop}`)) {
				params![prop] = value as string | number | boolean;
			} else if (
				!propBlacklist.includes(prop) &&
				(typeof value === 'string' || value instanceof String)
			) {
				query[prop] = value as string;
			}
		});

		Object.entries(params || {}).forEach(([param, value]) => {
			if (!path.includes(`:${param}`) && !props?.hasOwnProperty(param)) {
				query[param] = String(value);
			}

			path = path.replace(`:${param}`, String(value));
		});

		let queryParams = new URLSearchParams(query).toString();

		if (queryParams.length > 0) {
			path = path + '?' + queryParams;
		}

		return {
			href: '/' + path.replace(/^\/+/, ''),
			route: namedRoute,
		};
	}

	return { href: href || '', route: {} };
};

// Protocols that should be handed to the OS (dialer, mail client, calendar…)
// rather than navigated to or opened through the in-app browser.
const osHandledProtocols = ['webcal', 'mailto', 'tel', 'sms'];

export const isOsHandledUrl = (url: string | undefined | null): boolean => {
	if (!url) return false;

	try {
		const u = new URL(url);

		return osHandledProtocols.some((p) => u.protocol === `${p}:`);
	} catch {
		return false;
	}
};

export const isLocalPathOrUrl = (url: string | undefined | null): boolean => {
	if (!url || url === undefined) return true;

	// Catch some none-URL options first, such as relative links or JS triggers.
	if (url.startsWith('/') && !url.startsWith('//')) {
		return true;
	} else if (url.startsWith('#')) {
		return true;
	} else if (url.startsWith('javascript')) {
		return true;
	}

	try {
		const u = new URL(url);

		// List of protocols that the app should handle
		if (['threesixty', 'capacitor'].some((p) => u.protocol === `${p}:`)) {
			return true;
		}

		if (isOsHandledUrl(url)) {
			return false;
		}

		if (window.location.hostname === u.hostname) {
			return true;
		}

		if (
			!u.hostname ||
			u.hostname === 'localhost' ||
			u.hostname.endsWith('.local')
		) {
			return true;
		}

		return false;
	} catch {
		return false;
	}
};

export const shouldConfirmNavigation = (): boolean =>
	!!sessionStorage.getItem(ROUTER_CONFIRM_NAVIGATION_KEY);

export const setConfirmNavigation = (
	confirmNavigation: string | null
): void => {
	if (confirmNavigation) {
		sessionStorage.setItem(ROUTER_CONFIRM_NAVIGATION_KEY, confirmNavigation);
	} else {
		sessionStorage.removeItem(ROUTER_CONFIRM_NAVIGATION_KEY);
	}
};

export const confirmNavigation = (
	didNotConfirm: () => void = () => {}
): boolean => {
	const confirmNavigation = sessionStorage.getItem(
		ROUTER_CONFIRM_NAVIGATION_KEY
	);

	if (confirmNavigation) {
		const didConfirm = confirm(confirmNavigation);

		if (didConfirm) {
			setConfirmNavigation(null);
		} else {
			didNotConfirm();
		}

		return didConfirm;
	}

	return true;
};
